import api from "./axios";

/**
 * A page of students.
 *
 * `pageNo` is 1-based, matching the backend (it computes `firstResult = (pageNo - 1) * pageSize`)
 * and every other paginated service here. This used to take a 0-based `page` and translate it
 * internally, which silently broke callers that passed `pageNo` like they do everywhere else:
 * the unknown key was dropped, `page` fell back to its default, and every request asked for
 * page 1 — so the students table's Next button re-fetched the first page forever.
 */
export async function getStudents({
                                      pageNo = 1,
                                      pageSize = 10,
                                      search = "",
                                      studentNumber = "",
                                      firstName = "",
                                      lastName = "",
                                      phone = "",
                                      includeDisabled = false
                                  } = {}) {
    const params = {
        pageNo,
        pageSize,
    };

    if (includeDisabled) {
        params.includeDisabled = true;
    }

    // Free-text across name + student number; the picker's single search box uses this.
    if (search && search.trim().length > 0) {
        params.search = search.trim();
    }

    if (studentNumber && studentNumber.trim().length > 0) {
        params.studentNumber = studentNumber.trim();
    }
    if (firstName && firstName.trim().length > 0) {
        params.firstName = firstName.trim();
    }
    if (lastName && lastName.trim().length > 0) {
        params.lastName = lastName.trim();
    }
    if (phone && phone.trim().length > 0) {
        params.phone = phone.trim();
    }

    const res = await api.get("/students", {params});

    return res.data;
}

/**
 * Student numbers of every student matching a filter, unpaged — for "select all matching" in a
 * picker that pages the server. Returns strings only, never student rows.
 */
export async function getStudentNumbers({search = ""} = {}) {
    const params = {};
    if (search && search.trim().length > 0) params.search = search.trim();
    const res = await api.get("/students/numbers", {params});
    return res.data;
}

export async function createStudent(student) {
    const res = await api.post("/students", student);
    return res.data;
}

export async function updateStudent(student) {
    const res = await api.put("/students", student);
    return res.data;
}

export async function disableStudent(studentNumber) {
    const res = await api.patch(`/students/${studentNumber}/disable`);
    return res.data;
}

export async function getStudentDetail(studentNumber) {
    const res = await api.get(`/students/${studentNumber}`);
    return res.data;
}

export async function getStudentSubjectAssignments(studentNumber) {
    const res = await api.get(`/students/${studentNumber}/subject-assignments`);
    return res.data;
}

export async function assignSubjectsToStudent(studentNumber, subjectIds) {
    const res = await api.post(`/students/${studentNumber}/subject-assignments`, {
        subjectIds,
    });
    return res.data;
}

export async function deleteSubjectFromStudent(studentNumber, subjectId) {
    const res = await api.delete(`/students/${studentNumber}/subject-assignments/${subjectId}`);
    return res.data;
}
